import React, {FC, useState} from 'react'
import AlertMessage from '../../../common/alert/AlertMsg'
import SharedData from '../../../common/SharedData'
import {enqueueBulkAdd, uploadImagesWithBulk} from '../core/_requests'
import {BulkOfferSeed} from '../core/extractBarcodes'
import {ICatalogVariantPreview} from '../../../offers/cors/_models'
import Loader from '../../../common/Loader'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'

/** One ambiguous (show_card) barcode the async ADD worker parked for the
 *  supplier to resolve on the Bulk Jobs review page. */
export type PendingVariantGroup = {
  barcode: string
  variants: ICatalogVariantPreview[]
  seed?: BulkOfferSeed
  /** Backend-tagged parked-row kind: `edit` groups resolve by updating the
   *  owned variant's offer; `add` (default) groups create offers. */
  action?: 'add' | 'edit'
}

type IProps = {
  bulkFile: any
  setBulkFile: React.Dispatch<any>
  imagesFile: any
  setImagesFile: React.Dispatch<any>
}
const SubmitButtonRow: FC<IProps> = ({bulkFile, setBulkFile, imagesFile, setImagesFile}) => {
  const {formatMessage} = useIntl()
  const {currentUser, language} = SharedData()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const isFormHasError = () => {
    let hasError = false
    setBulkFile({...bulkFile, error: false})
    setImagesFile({...imagesFile, error: false})
    if (!bulkFile.value && !imagesFile.value) {
      setBulkFile({...bulkFile, error: true})
      setImagesFile({...imagesFile, error: true})
      hasError = true
    }
    return hasError
  }

  const handleSubmit = async () => {
    if (isFormHasError()) return
    setIsLoading(true)
    try {
      if (bulkFile.value) {
        // Async: the sheet + optional image zip are queued and processed in the
        // background (new-product requests, zip attach, auto-listed offers, and
        // parked ambiguous variants). The supplier tracks progress + resolves any
        // pending variants on the Bulk Jobs page.
        const res: any = await enqueueBulkAdd(
          currentUser?.token,
          bulkFile.value,
          imagesFile.value || undefined,
          language
        )
        if (res?.data?.job_id) {
          navigate('/products/bulk-jobs')
        } else {
          setError(true)
        }
      } else if (imagesFile.value) {
        // Only a zip (no sheet): attach images to already-filed requests. This
        // stays synchronous — it's a small follow-up action, not a bulk add.
        await uploadImagesWithBulk(imagesFile.value, currentUser?.token)
        localStorage.setItem('thankYouPageURL', window.location.href)
        navigate('/thank-you?go=request-update-products')
      }
    } catch (exp) {
      setError(true)
    }
    setIsLoading(false)
  }

  return (
    <>
      <div className='apb-actionbar'>
        <div className='apb-actionbar-note'>
          <i className='fas fa-shield-halved' aria-hidden='true' />
          <span>{formatMessage({id: 'by_uploading_products_stnrd'})}</span>
        </div>
        <div className='apb-actionbar-btns'>
          <button type='button' className='apb-btn-cancel' onClick={() => navigate(-1)}>
            {formatMessage({id: 'cancel'})}
          </button>
          <button type='button' onClick={handleSubmit} className='apb-btn-upload'>
            {formatMessage({id: 'upload'})}
          </button>
        </div>
      </div>
      {isLoading && <Loader isFull={true} />}
      {error === true && (
        <AlertMessage
          title={formatMessage({id: 'something_wrong_title'})}
          message={formatMessage({id: 'something_wrong_desc'})}
          onClose={() => setError(false)}
        />
      )}
    </>
  )
}

export default SubmitButtonRow
